declare const WebPhone: any;

import { Injectable, OnDestroy } from '@angular/core';
import { SettingService } from './setting.service';
import { NavigationEnd, Router } from '@angular/router';
import { Helper } from '../utility/Helper';

@Injectable({
  providedIn: 'root'
})
export class WebphoneScriptService implements OnDestroy {
  private webPhone: any;
  private enableWebPhone: boolean = false;
  private scriptId: any;
  private scriptSrc: any;
  private scriptAttributes: any;
  private settingScripts: any;
  private routeSubscription: any;
  private validRoutes = ['/calling-contact-action', '/call-center'];
  public script: any;
  private isPopupCallExist: boolean = false;
  //This is a flag to check if the webphone is disabled on the valid routes
  isDisableWebphoneOnValidRoutes: boolean = false;

  constructor(
    private settingService: SettingService,
    private router: Router

  ) {
    // Subscribe to route changes
    this.routeSubscription = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.isDisableWebphoneOnValidRoutes = false;
        this.checkRouteAndUpdateWebPhone();
      }
    });
  }

  ngOnDestroy() {
    if (this.routeSubscription) {
      this.routeSubscription.unsubscribe();
    }
  }

  async ensureWebPhoneInitialized(): Promise<boolean> {
    try {
      return await new Promise<boolean>((resolve) => {
        if (typeof WebPhone !== 'undefined' && this.webPhone) {
          resolve(true);
          return;
        }

        if (this.script?.readyState === 'complete' || this.script?.readyState === 'loaded') {
          resolve(typeof WebPhone !== 'undefined' && this.webPhone !== null);
          return;
        }

        const maxAttempts = 20;
        let attempts = 0;

        const checkWebPhone = () => {
          attempts++;
          if (typeof WebPhone !== 'undefined' && this.webPhone) {
            resolve(true);
          } else if (attempts >= maxAttempts) {
            resolve(false);
          } else {
            setTimeout(checkWebPhone, 500); // Check every 500ms
          }
        };

        // Khởi tạo WebPhone nếu chưa được khởi tạo
        this.initializeWebPhone().then(() => {
          checkWebPhone();
        }).catch(() => {
          resolve(false);
        });
      });
    } catch (error) {
      console.error('Error in ensureWebPhoneInitialized:', error);
      return false;
    }
  }

  //Set status isPopupCallExist
  setIsPopupCall(isCalling: boolean) {
    this.isPopupCallExist = isCalling;
  }

  // Check route and update WebPhone
  async checkRouteAndUpdateWebPhone() {
    //Check the setting of keep webphone open
    let settingPoolEnableWebphone = await this.settingService.getSettingByKeyAndGroup('ENABLE_WEBPHONE', 'CAL_WEBPHONE_CONFIG').toPromise();
    let settingEnableWebphone = settingPoolEnableWebphone.result.value == '1' ? true : false;

    //If the setting of enable webphone is false, return
    if (!settingEnableWebphone) {
      return;
    }

    //If isPopupCallExist is true, keep the WebPhone open
    if (this.isPopupCallExist) {
      return;
    }
    else {
      const allowedRoutes = this.validRoutes;
      const currentRoute = this.router.url;

      if (!allowedRoutes.some(route => currentRoute.includes(route))) {
        // if invalid route, logout WebPhone
        if (this.webPhone) {
          this.deleteWebPhoneScript();
        }
      } else {
        // if valid route, initialize WebPhone
        if (this.webPhone) {
          this.initializeWebPhone();
        }
      }
    }
  }

  //Delete WebPhone script
  deleteWebPhoneScript() {
    // this.webPhone?.discard();

    document.getElementById(this.scriptId)?.remove();
    document.getElementById("toggleButtonWebPhone")?.remove();
    document.getElementById("webphoneContainer")?.remove();
    document.getElementById("dragButtonWebPhone")?.remove();

    this.webPhone = null;
  }

  // Initialize WebPhone
  async initializeWebPhone(skipRouteCheck: boolean = false) {
    try {
      const allowedRoutes = this.validRoutes;
      const currentRoute = this.router.url;

      if (!skipRouteCheck) {
        if (!allowedRoutes.some(route => currentRoute.includes(route))) {
          this.deleteWebPhoneScript();
          return;
        }
      }

      const existingScript = document.getElementById(this.scriptId);

      if (typeof WebPhone !== 'undefined' && this.webPhone && (await this.checkIsDiscarded())) {
        console.log('WebPhone already initialized');
        return;
      }

      let response = await this.settingService.getSettingByGroupName('CAL_WEBPHONE_CONFIG').toPromise();
      this.settingScripts = response.result;
      if (response.result && response.result.length > 0) {
        this.enableWebPhone = response.result.find(x => x.key == 'ENABLE_WEBPHONE')?.value == '1' ? true : false;
        this.scriptId = response.result.find(x => x.key == 'WEBPHONE_ID')?.value;
        this.scriptSrc = response.result.find(x => x.key == 'WEBPHONE_URL')?.value;
        this.scriptAttributes = response.result.find(x => x.key == 'WEBPHONE_ATTRIBUTE')?.value;
      }

      //If the WebPhone script is not loaded, create it
      if (this.enableWebPhone && !existingScript) {
        this.script = document.createElement('script');
        this.script.id = this.scriptId;
        this.script.src = this.scriptSrc;

        if (this.scriptAttributes) {
          const attributes = this.scriptAttributes.split(',');
          attributes.forEach(attr => {
            const [key, value] = attr.split(';');
            this.script.setAttribute(key, value);
          });
        }

        this.script.addEventListener('load', () => {
          // if (this.isDisableWebphoneOnValidRoutes) {
          //   console.log('Webphone script disabled on valid routes');
          //   this.deleteWebPhoneScript();
          // }
          // else {
          console.log('WebPhone script loaded');
          this.webPhone = new WebPhone();
          // }
        });

        this.script.addEventListener('error', (error) => {
          console.error('Error loading WebPhone script:', error);
        });

        document.body.appendChild(this.script);
      }
      //If the WebPhone script is already loaded, initialize it
      else if (this.enableWebPhone && existingScript) {
        await this.initWebPhoneAfterDiscarded();
      }
    }
    catch (error) {
      console.error('Error initializing WebPhone:', error);
    }
  }

  async initWebPhoneAfterDiscarded() {
    const isInitialized = await this.ensureWebPhoneInitialized();
    if (!isInitialized) {
      return;
    }

    if (this.enableWebPhone && this.webPhone) {
      if (this.webPhone?.isDiscarded) {
        await this.webPhone.init();
      }
    }
  }

  async checkIsDiscarded(): Promise<boolean> {
    const isInitialized = await this.ensureWebPhoneInitialized();
    if (!isInitialized) {
      return true;
    }

    if (this.enableWebPhone && this.webPhone) {
      return this.webPhone.isDiscarded;
    }
  }


  async login(extension: string, password: string) {
    const isInitialized = await this.ensureWebPhoneInitialized();
    if (!isInitialized) {
      return;
    }

    if (this.enableWebPhone && this.webPhone) {
      let server: string = '';
      let port: string = '';
      let webSocketType: string = '';

      let webphoneLoginAttr = this.settingScripts.find(x => x.key == 'WEBPHONE_LOGIN_ATTRIBUTE')?.value;
      if (!Helper.isNullOrEmpty(webphoneLoginAttr)) {
        const pairs = webphoneLoginAttr?.split(',');
        pairs.forEach(pair => {
          // Split each pair by semicolon to separate key and value
          const [key, value] = pair.split(';');

          switch (key) {
            case 'server':
              server = value;
              break;
            case 'port':
              port = value;
              break;
            case 'webSocketType':
              webSocketType = value;
              break;
          }
        });

        await this.webPhone.login(server, port, webSocketType, extension, password, false);
      }
    }
  }

  async loginCallback(extension: string, password: string, callback: Function) {
    const isInitialized = await this.ensureWebPhoneInitialized();
    if (!isInitialized) {
      return;
    }

    if (this.enableWebPhone && this.webPhone) {
      let server: string = '';
      let port: string = '';
      let webSocketType: string = '';

      let webphoneLoginAttr = this.settingScripts.find(x => x.key == 'WEBPHONE_LOGIN_ATTRIBUTE')?.value;
      if (!Helper.isNullOrEmpty(webphoneLoginAttr)) {
        const pairs = webphoneLoginAttr?.split(',');
        pairs.forEach(pair => {
          // Split each pair by semicolon to separate key and value
          const [key, value] = pair.split(';');

          switch (key) {
            case 'server':
              server = value;
              break;
            case 'port':
              port = value;
              break;
            case 'webSocketType':
              webSocketType = value;
              break;
          }
        });

        await this.webPhone.loginCallback(server, port, webSocketType, extension, password, false, callback);
      }
    }
  }

  async makeCall(number: string) {
    const isInitialized = await this.ensureWebPhoneInitialized();
    if (!isInitialized) {
      return;
    }

    if (this.enableWebPhone && this.webPhone) {
      if (Helper.isNullOrEmpty(number)) {
        return;
      }

      number = number.replace(/[^0-9]/g, '');
      await this.webPhone.makeCall(number);
    }
  }

  async checkLogin() {
    const isInitialized = await this.ensureWebPhoneInitialized();
    if (!isInitialized) {
      return;
    }

    if (this.enableWebPhone && this.webPhone) {
      return this.webPhone.registed;
    }
  }

  async logout() {
    const isInitialized = await this.ensureWebPhoneInitialized();
    if (!isInitialized) {
      return;
    }

    if (this.enableWebPhone && this.webPhone) {
      await this.webPhone.logout();
    }
  }

  async openPhone() {
    const isInitialized = await this.ensureWebPhoneInitialized();
    if (!isInitialized) {
      return;
    }

    if (this.enableWebPhone && this.webPhone) {
      await this.webPhone.openPhone();
    }
  }

  async closePhone() {
    const isInitialized = await this.ensureWebPhoneInitialized();
    if (!isInitialized) {
      return;
    }

    if (this.enableWebPhone && this.webPhone) {
      await this.webPhone.closePhone();
    }
  }
}
